import { Button, Col, Grid, Icon, NetworkIcon, Tooltip } from 'gu-ui-kit'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link } from 'react-router-dom'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import { useGetConnectionLink } from 'utils/hooks/useGetConnectionLink'
import styles from 'modules/Services/Network/network.module.scss'
import AccountList from './subcomponents/AccountsList'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Services/Network/subcomponents/AccountsList/filters'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import GridSpinner from 'components/UI/GridSpinner'
import SERVICES_API_V1 from 'api/services/v1'
import { useParams } from 'react-router'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'
import {
  FETCH_NETWORK_ACCOUNTS,
  FETCH_NETWORK_DATA,
  NETWORK_ACCOUNTS_CLEAN,
  SET_ACCOUNTS_FILTERS,
  SET_NETWORK_DATA,
  UPDATE_ACCOUNTS,
} from 'modules/Services/types'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import omit from 'lodash/omit'
import EmptyResults from 'components/EmptyResults'
import NetworkItemHeader from 'modules/Services/Network/subcomponents/NetworkItemHeader'
import NetworkBalanceColumn from 'modules/Services/Network/subcomponents/NetworkBalanceColumn'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PropTypes from 'prop-types'

const LeftColumn = ({ children }) => {
  const breakpoints = useBreakpoint()

  return !breakpoints.md ? (
    <Col flexGrow={1}>{children}</Col>
  ) : (
    <Grid flexDirection={'column'} fluid>
      {children}
    </Grid>
  )
}

LeftColumn.propTypes = {
  children: PropTypes.node,
}

const Network = () => {
  const dispatch = useDispatch()
  const pathGen = useGeneratePathI18n()
  const { networkType } = useParams()
  const { t } = useTranslation(['services'])
  const { cancelableDispatch } = useCancelToken()
  const breakpoints = useBreakpoint()
  const connectionLink = useGetConnectionLink(networkType)

  const title = useMemo(
    () =>
      networkType
        ? `${t('common::nav_menu.advertisement_networks')} — ${t(
            'services::networks.' + networkType,
          )}`
        : t('common::nav_menu.advertisement_networks'),
    [networkType],
  )

  const {
    network: { display_balance = true, loading, homepage_link, has_account, accounts },
  } = useSelector((state) => ({
    network: state['services/network'],
    notifications: state['recommendations/audits'].notifications,
  }))

  const [accountListConfig, setAccountListConfig] = useLocalStorage('accountListConfig', {})
  const { networkId } = useNetworksData(networkType)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['services/network'].filters),
    config: config(),
    setAction: SET_ACCOUNTS_FILTERS,
    additionalFilterState: accountListConfig?.[networkType],
    localStorageKey: 'services/network',
  })

  useEffect(() => {
    if (networkId) {
      cancelableDispatch({
        type: FETCH_NETWORK_DATA,
        payload: {
          id: networkId,
        },
      })
    }
  }, [networkId])

  useEffect(
    () =>
      setAccountListConfig({
        ...accountListConfig,
        [networkType]: { ...filterManager.filters },
      }),
    [filterManager.filters, networkType],
  )

  useEffect(() => {
    networkId &&
      SERVICES_API_V1.getNetworkSettings({ networkId })
        .then(({ response }) =>
          dispatch({
            type: SET_NETWORK_DATA,
            payload: {
              bulk_account_creation_allowed: response?.data?.bulk_account_creation_allowed,
            },
          }),
        )
        .catch((err) => console.logXHR(err))
  }, [networkId])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(
    omit(filterManager.params, ['is_archived']), //игнорируем фильтр архива
    has_account ? [1] : [],
    !loading,
  )

  const [page, setPage] = useState(1)

  const { pagination, sorting, loading: accountsLoading } = accounts

  const composedParams = useMemo(
    () => ({
      ...(filterManager?.params || {}),
      sort: sorting.prop,
      direction: sorting.direction,
      offset: (page - 1) * pagination.limit,
      limit: pagination.limit,
      network_id: networkId,
    }),
    [filterManager.params, sorting, page],
  )

  useEffect(() => {
    dispatch({ type: NETWORK_ACCOUNTS_CLEAN })
  }, [sorting, filterManager.params, networkType])

  useEffect(() => {
    dispatch({
      type: UPDATE_ACCOUNTS,
      payload: { composedParams },
    })
  }, [composedParams])

  useEffect(() => {
    if (accountsLoading || !filterManager.params) {
      return
    }

    dispatch({
      type: FETCH_NETWORK_ACCOUNTS,
    })
  }, [filterManager.params, composedParams])

  return (
    <>
      <Helmet defer={false}>
        <title>{title}</title>
      </Helmet>

      {firstLoad ? (
        <GridSpinner width={32} className={'margin-top-64'} />
      ) : (
        <>
          <Grid gap={breakpoints.ls ? 32 : 64} wrap={false}>
            <LeftColumn>
              <NetworkItemHeader
                title={title}
                totallyEmptyList={totallyEmptyList}
                networkType={networkType}
                networkId={networkId}
              />

              {totallyEmptyList ? (
                <EmptyResults
                  className={styles['empty-results']}
                  header={t('services::networks.' + networkType)}
                  text={
                    <>
                      {t('services::account_list.empty_list.text')}
                      {homepage_link && (
                        <Tooltip
                          title={t(`services::about_network_tooltip`)}
                          placement={'bottom'}
                          activeClassName={styles['info-icon']}
                        >
                          <a href={homepage_link} target={'_blank'} rel="noreferrer">
                            <Icon size={'m'} name={'information'} color={'Interface-Seventh'} />
                          </a>
                        </Tooltip>
                      )}
                    </>
                  }
                  image={<NetworkIcon name={networkType} rounded />}
                  actionButton={
                    <Button
                      startIcon={<Icon size={'m'} name={'plus'} />}
                      component={Link}
                      to={pathGen(connectionLink, { networkType })}
                      variant={'contained'}
                      size={'m'}
                    >
                      {t('services::account_list.empty_list.button_label')}
                    </Button>
                  }
                />
              ) : (
                <AccountList
                  display_balance={display_balance}
                  networkId={networkId}
                  networkType={networkType}
                  filterManager={filterManager}
                  accounts={accounts}
                  setPage={setPage}
                />
              )}
            </LeftColumn>

            {!breakpoints.md && (
              <>
                {!totallyEmptyList && (
                  <Col flexGrow={1} className={styles['right-column']}>
                    <NetworkBalanceColumn networkType={networkType} />
                  </Col>
                )}
              </>
            )}
          </Grid>
        </>
      )}
    </>
  )
}

export default Network
